import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button,
  Box,
  InputAdornment,
  IconButton
} from '@mui/material';
import Heading from '../../../WebApp/Components/Heading/heading.tsx';
import MapModal from '../../../WebApp/Pages/Warehouses/map-modal.tsx'; 
import mapsIcon from '../../../assets/map.png';

interface EditStopNameModalProps {
  open: boolean;
  initialName: string;
  initialLat?: number;
  initialLng?: number;
  status: string;
  onClose: () => void;
  onSave: (newName: string, newLat?: string, newLng?: string) => void;
}

const EditStopNameModal: React.FC<EditStopNameModalProps> = ({ 
  open, 
  initialName, 
  initialLat,
  initialLng,
  status,
  onClose, 
  onSave 
}) => {
  const [stopName, setStopName] = useState<string>('');
  const [lat, setLat] = useState<string>('');
  const [lng, setLng] = useState<string>('');
  const [isMapOpen, setIsMapOpen] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setStopName(initialName);
      setLat(initialLat ? String(initialLat) : '');
      setLng(initialLng ? String(initialLng) : '');
    }
  }, [open, initialName, initialLat, initialLng]);

  const handleLocationSelect = (selectedAddress: string, selectedLat: string, selectedLng: string) => {
    setLat(selectedLat);
    setLng(selectedLng);
  };

  const handleSave = () => {
    onSave(stopName, lat, lng);
  };

  const initialLocation = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="xs"
      >
        <Heading 
          title="Edit Stop" closeIcon="true"
          onClose={onClose} 
        />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              autoFocus
              margin="dense"
              label="Stop Name"
              type="text"
              fullWidth
              variant="outlined"
              value={stopName}
              onChange={(e) => setStopName(e.target.value)}
              size="small"
            />

            {status?.toLowerCase() === 'approved' && (
              <>
                <TextField 
                  label="Latitude" 
                  value={lat} 
                  onChange={(e) => setLat(e.target.value)}
                  placeholder="Auto-filled from map"
                  fullWidth
                  variant="outlined"
                  size="small" 
                />
                
                <TextField 
                  label="Longitude"
                  value={lng} 
                  onChange={(e) => setLng(e.target.value)}
                  placeholder="Auto-filled from map"
                  fullWidth
                  variant="outlined"
                  size="small" 
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => setIsMapOpen(true)} 
                          color="primary" edge="end" title="Select location on map"
                        >
                          <img src={mapsIcon} alt="Map View" style={{ width: '30px', height: '30px' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }} 
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <Button onClick={onClose} color="inherit" className="custom-cancel-button" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={!stopName.trim()} 
            className="custom-button"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <MapModal 
        open={isMapOpen} 
        onClose={() => setIsMapOpen(false)} 
        onLocationSelect={handleLocationSelect}
        initialLocation={initialLocation}
      />
    </>
  );
};

export default EditStopNameModal;